import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAuthenticate } from 'features/user/hooks/useAuthenticate'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { Schema } from '../../schema'
import { CONFIRM_USER, Data } from './api'
import { UseConfirmUser } from './types'

export const useConfirmUser = (): UseConfirmUser => {
  const { t } = useTranslation('features/user')
  const { authenticate } = useAuthenticate()
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const ability = useAbility()

  const { isPending, mutateAsync } = useMutation<{ input: Schema }, Data>({
    mutation: CONFIRM_USER(ability),
    onCompleted: async (data: Data): Promise<void> => {
      const {
        confirmUser: { accessToken, errors, refreshToken, user },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
      } else if (user && accessToken && refreshToken) {
        setErrors({})
        authenticate({ accessToken, refreshToken, user })
        enqueueSnackbar(t('completeSetup.form.success'), {
          variant: 'success',
        })
      }
    },
  })

  const confirm = (data: Schema): void => {
    void mutateAsync({
      input: data,
    })
  }

  return {
    confirm,
    errors,
    loading: isPending,
  }
}
